<script lang="ts">
    /**
     * InkPage - Ink 대문 (/ink)
     *
     * 🎯 JadEye(/jadeye)·Gridverse(/gridverse)·Pin(/pin)과 **같은 층**이다 — 랜딩 SPA 안의 한 페이지라
     *    앱바·푸터가 저절로 공유된다. 실제 서비스(서가 · 작품 홈)는 셸 밖의 서버 렌더 화면이다.
     *
     * 짜임은 PinPage 를 따른다: 머리 → 「무엇을 읽으실래요?」 카드 → 작품마다 한 묶음 → 「Ink의 약속」.
     *
     * 🚨 작품을 여기에 손으로 적지 않는다 — 목록 · 문구는 전부 서버 주입값(window.JadEye.ink ← config/ink.php)이다.
     *    작품이 늘면 카드와 묶음이 저절로 늘어난다.
     * 🚨 머리 제목 한 줄만은 여기 글자로 있다 — SeoTest 가 noscript 의 제목과 대조한다(서버 값은 ink.tagline).
     * 🚨 카피는 "지금 실제로 있는 것"만 말한다. 번역 회차가 공개되기 전에는 「1회부터 읽기」 같은 단추를 두지 않는다.
     */
    import { getInk } from '../../../lib/ink';

    const ink = getInk();
    const works = ink?.works ?? [];

    let visible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            visible = true;
        }, 100);
        return () => clearTimeout(t);
    });

    function scrollToWorks(e: MouseEvent) {
        e.preventDefault();
        document.querySelector('#ink-works')?.scrollIntoView({ behavior: 'smooth' });
    }
</script>

<!-- title·description 은 config/seo.php 가 SSOT (LandingApp.applyMeta 참고) -->

<div class="ink-page">
    <section class="ink-hero">
        <div class="hero-inner" class:visible>
            <span class="hero-eyebrow">Ink</span>
            <h1 class="hero-title">이야기가 번지는 곳</h1>
            <p class="hero-subtitle">
                {#each ink?.subtitle ?? [] as line, i}
                    {#if i > 0}<br />{/if}{line}
                {/each}
            </p>

            {#if ink}
                <div class="hero-actions">
                    <a href={ink.shelf} class="hero-cta-primary">
                        서가 보기
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                    </a>
                    <a href="#ink-works" class="hero-cta-secondary" onclick={scrollToWorks}>
                        <span>무엇을 읽을 수 있나요?</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M12 5v14" /><path d="m19 12-7 7-7-7" />
                        </svg>
                    </a>
                </div>
            {/if}
        </div>
    </section>

    {#if works.length > 0}
        <!-- 작품 메뉴 — Ink 의 모든 작품은 여기서 출발한다 -->
        <section id="ink-works" class="works-section" aria-label="Ink 작품">
            <div class="works-inner">
                <h2 class="works-title">무엇을 읽으실래요?</h2>
                <div class="works-grid">
                    {#each works as work (work.slug)}
                        <a class="work-card" href={work.path}>
                            <span class="work-body">
                                <span class="work-card-title">{work.label}</span>
                                <span class="work-card-desc">{work.tagline}</span>
                            </span>
                            <svg class="work-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                            </svg>
                        </a>
                    {/each}
                </div>
            </div>
        </section>
    {/if}

    <!-- 작품마다 한 묶음 — 배경 톤을 번갈아 경계를 준다 -->
    {#each works as work, i (work.slug)}
        <section class="concept-section" class:concept-section-alt={i % 2 === 1}>
            <div class="concept-inner" class:visible>
                <div class="concept-head">
                    <h2 class="concept-title">{work.label}</h2>
                    <p class="concept-subtitle">{work.summary}</p>
                </div>

                <div class="concept-grid">
                    {#each work.points as point}
                        <div class="concept-card">
                            <span class="concept-emoji">{point.icon}</span>
                            <h3 class="concept-card-title">{point.title}</h3>
                            <p class="concept-card-desc">{point.desc}</p>
                        </div>
                    {/each}
                </div>

                <div class="concept-cta">
                    <a href={work.path} class="concept-cta-link">
                        {work.cta}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                    </a>
                </div>
            </div>
        </section>
    {/each}

    {#if ink && ink.promises.length > 0}
        <section class="concept-section" class:concept-section-alt={works.length % 2 === 1}>
            <div class="concept-inner" class:visible>
                <div class="concept-head">
                    <h2 class="concept-title">Ink의 약속</h2>
                </div>

                <div class="concept-grid">
                    {#each ink.promises as promise}
                        <div class="concept-card">
                            <span class="concept-emoji">{promise.icon}</span>
                            <h3 class="concept-card-title">{promise.title}</h3>
                            <p class="concept-card-desc">{promise.desc}</p>
                        </div>
                    {/each}
                </div>
            </div>
        </section>
    {/if}
</div>

<style>
    /* Ink 의 색 — 먹빛이 도는 남색 #3F4A72 (app.css 의 --color-ink-600 과 같은 값) */
    .ink-page {
        width: 100%;
        --ink: #3f4a72;
        --ink-rgb: 63, 74, 114;
    }

    .ink-hero {
        min-height: 62vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 7rem 1.5rem 3rem;
        background: linear-gradient(135deg, #f1f2f7 0%, #eaecf3 30%, #fafbfc 100%);
        position: relative;
        overflow: hidden;
    }

    .ink-hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -30%;
        width: 80%;
        height: 80%;
        background: radial-gradient(circle, rgba(var(--ink-rgb), 0.08) 0%, transparent 70%);
        pointer-events: none;
    }

    .hero-inner {
        max-width: 48rem;
        margin: 0 auto;
        width: 100%;
        text-align: center;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .hero-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .hero-eyebrow {
        display: inline-block;
        font-size: 0.9rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        color: var(--ink);
        background: rgba(var(--ink-rgb), 0.1);
        padding: 0.4rem 1rem;
        border-radius: 999px;
        margin-bottom: 1.5rem;
    }

    .hero-title {
        font-size: clamp(2.2rem, 5vw, 3.5rem);
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: -0.03em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 1.5rem;
    }

    .hero-subtitle {
        font-size: 1.1rem;
        color: var(--landing-text-sub, #666666);
        line-height: 1.8;
        margin: 0 0 2.25rem;
    }

    .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        justify-content: center;
        align-items: center;
    }

    .hero-cta-primary {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.9rem 1.8rem;
        background: var(--ink);
        color: #ffffff;
        font-size: 1rem;
        font-weight: 600;
        text-decoration: none;
        border-radius: 999px;
        box-shadow: 0 8px 24px rgba(var(--ink-rgb), 0.25);
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
    }

    .hero-cta-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(var(--ink-rgb), 0.32);
    }

    .hero-cta-secondary {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.9rem 1.4rem;
        color: var(--landing-text-sub, #666666);
        font-size: 0.95rem;
        font-weight: 500;
        text-decoration: none;
        border-radius: 999px;
        transition:
            color 0.2s ease,
            background 0.2s ease;
    }

    .hero-cta-secondary:hover {
        color: var(--ink);
        background: rgba(var(--ink-rgb), 0.07);
    }

    .works-section {
        padding: 3.5rem 1.5rem 0;
        background: var(--landing-bg, #ffffff);
    }

    .works-inner {
        max-width: 60rem;
        margin: 0 auto;
    }

    .works-title {
        font-size: clamp(1.4rem, 3vw, 1.8rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        text-align: center;
        margin: 0 0 1.75rem;
    }

    .works-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .work-card {
        display: flex;
        align-items: center;
        gap: 1.1rem;
        padding: 1.5rem 1.75rem;
        background: var(--landing-bg-alt, #f8f9fa);
        border: 1px solid rgba(var(--ink-rgb), 0.18);
        border-radius: 18px;
        text-decoration: none;
        transition:
            transform 0.25s cubic-bezier(0.16, 1, 0.3, 1),
            border-color 0.2s ease,
            box-shadow 0.25s ease;
    }

    .work-card:hover {
        transform: translateY(-3px);
        border-color: rgba(var(--ink-rgb), 0.45);
        box-shadow: 0 10px 26px rgba(var(--ink-rgb), 0.12);
    }

    .work-body {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
        min-width: 0;
    }

    .work-card-title {
        font-size: 1.12rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
    }

    .work-card-desc {
        font-size: 0.9rem;
        line-height: 1.55;
        color: var(--landing-text-sub, #666666);
    }

    .work-arrow {
        margin-left: auto;
        flex-shrink: 0;
        color: var(--ink);
    }

    .concept-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg, #ffffff);
    }

    .concept-section-alt {
        background: var(--landing-bg-alt, #f8f9fa);
    }

    .concept-section-alt .concept-card {
        background: var(--landing-bg, #ffffff);
    }

    .concept-inner {
        max-width: 72rem;
        margin: 0 auto;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .concept-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .concept-head {
        text-align: center;
        margin-bottom: 3rem;
    }

    .concept-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.75rem;
    }

    .concept-subtitle {
        font-size: 1.05rem;
        line-height: 1.7;
        color: var(--landing-text-sub, #666666);
        max-width: 40rem;
        margin: 0 auto;
    }

    .concept-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.5rem;
        max-width: 60rem;
        margin: 0 auto;
    }

    .concept-card {
        background: var(--landing-bg-alt, #f8f9fa);
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 20px;
        padding: 2rem;
        text-align: center;
        transition:
            transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 0.3s ease;
    }

    .concept-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.07);
    }

    .concept-emoji {
        display: inline-block;
        font-size: 2.5rem;
        line-height: 1;
        margin-bottom: 1rem;
    }

    .concept-card-title {
        font-size: 1.2rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.6rem;
    }

    .concept-card-desc {
        font-size: 0.95rem;
        line-height: 1.7;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    .concept-cta {
        margin-top: 3rem;
        text-align: center;
    }

    .concept-cta-link {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        padding: 0.85rem 1.7rem;
        border: 1px solid rgba(var(--ink-rgb), 0.35);
        border-radius: 999px;
        color: var(--ink);
        font-size: 0.98rem;
        font-weight: 600;
        text-decoration: none;
        transition:
            background 0.2s ease,
            gap 0.2s ease;
    }

    .concept-cta-link:hover {
        background: rgba(var(--ink-rgb), 0.08);
        gap: 0.65rem;
    }

    @media (min-width: 768px) {
        .ink-hero {
            padding: 7rem 2.5rem 3rem;
        }

        .works-section {
            padding: 4.5rem 2.5rem 0;
        }

        .works-grid {
            grid-template-columns: repeat(2, 1fr);
        }

        .concept-section {
            padding: 6rem 2.5rem;
        }

        .concept-grid {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    @media (min-width: 1024px) {
        .ink-hero {
            padding: 7rem 3rem 3rem;
        }

        .concept-section {
            padding: 7rem 3rem;
        }
    }
</style>
